import React, { useState, useRef } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Loader2, Download } from 'lucide-react';
import * as API from '../../../api';
import { isSuperAdmin } from '../../../modules/auth/core/Auth';

interface HelpModalProps {
  reportName: string;
  pdfUrl?: string;
  fileName?: string;
  onClose: () => void;
  onUploadSuccess: (pdfUrl: string) => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({
  reportName,
  pdfUrl,
  fileName,
  onClose,
  onUploadSuccess
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // permite re-selecionar o mesmo arquivo depois
    event.target.value = '';

    if (file.type !== 'application/pdf') {
      setUploadError('Por favor, selecione apenas arquivos PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('O arquivo deve ter no máximo 10MB');
      return;
    }

    setIsUploading(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      // ✅ novo endpoint
      const response: any = await API.uploadFile(file);

      // tenta encontrar a URL em formatos comuns
      const uploadedUrl =
        response?.data?.pdf_url ??
        response?.data?.url ??
        response?.data?.file_url ??
        response?.data?.path ??
        response?.pdf_url ??
        response?.url ??
        response?.file_url ??
        response?.path;

      if (!uploadedUrl) {
        setUploadError('Upload concluído, mas não foi retornada a URL do PDF');
        return;
      }

      setUploadSuccess(true);
      onUploadSuccess(uploadedUrl);

      const addReportResponse = await API.addReportPDF({type:reportName, id_upload:response.data.id_upload});
      console.log(addReportResponse);
      
      console.log(reportName, response);
      

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error: any) {
      setUploadError(error?.message || 'Erro ao fazer upload do arquivo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) window.open(pdfUrl, '_blank');
  };

  return (
    <div className="relative bg-white rounded-xl shadow-2xl w-full max-h-[90vh] overflow-hidden m-0">
      <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 m-0">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              {pdfUrl ? 'Manual de Ajuda' : 'Adicionar Manual de Ajuda'}
            </h2>
            <p className="text-blue-100 text-sm">{reportName}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/20 rounded-lg transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </button>
      </div>

      <div className="p-6">
        {pdfUrl ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">PDF disponível para visualização</p>
                </div>
              </div>
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Baixar PDF
              </button>
            </div>

            <div className="bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
              <iframe
                src={pdfUrl}
                className="w-full h-[600px]"
                title="Manual de Ajuda"
              />
            </div>

            <div className="flex items-center justify-between">
              { isSuperAdmin() ? <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Upload className="w-4 h-4" />
                Atualizar PDF
              </button> : null }
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Fechar
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nenhum manual cadastrado
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Faça upload de um arquivo PDF com instruções e documentação para este relatório
              </p>
            </div>

            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all duration-200"
            >
              <div className="flex flex-col items-center gap-4">
                <div className="p-4 bg-blue-100 rounded-full">
                  <Upload className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900 mb-1">
                    Clique para selecionar um arquivo
                  </p>
                  <p className="text-sm text-gray-500">
                    Formato: PDF | Tamanho máximo: 10MB
                  </p>
                </div>
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />

            {uploadError && (
              <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-700">{uploadError}</p>
              </div>
            )}

            {uploadSuccess && (
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-700">PDF carregado com sucesso!</p>
              </div>
            )}

            {isUploading && (
              <div className="flex items-center justify-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <p className="text-sm text-blue-700">Fazendo upload do arquivo...</p>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={onClose}
                disabled={isUploading}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpModal;
